import { Link } from 'react-router-dom';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import type { RecentComment } from '@/services/api';
import { renderCommentContent } from '@/components/comments/CommentItem';

interface RecentCommentsProps {
  comments: RecentComment[];
  loading?: boolean;
  onInitiativeClick?: (initiativeId: string) => void;
  maxVisible?: number;
}

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}


function CommentCard({
  comment,
  onClick,
}: {
  comment: RecentComment;
  onClick?: () => void;
}) {
  const isReply = !!comment.parentId;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-all h-full flex flex-col"
    >
      {/* Header - Icon + Timestamp */}
      <div className="flex items-center gap-1.5 mb-1">
        <ChatBubbleLeftIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
        <span className="text-xs text-gray-400">
          {formatRelativeTime(comment.createdAt)}
        </span>
      </div>

      {/* Initiative title */}
      <div className="text-sm font-medium text-gray-900 truncate mb-0.5">
        {comment.initiative.title}
      </div>

      {/* Product path */}
      <div className="text-xs text-gray-500 truncate mb-2">
        {comment.initiative.subProduct.product.name} &gt; {comment.initiative.subProduct.name}
      </div>

      {/* Comment content */}
      <div className="text-xs text-gray-600 line-clamp-2 mb-2 flex-1">
        {isReply && <span className="text-gray-400">↳ </span>}
        "{renderCommentContent(comment.content)}"
      </div>

      {/* Commenter name */}
      <div className="text-xs text-gray-500 mt-auto">
        {comment.user.name}
      </div>
    </button>
  );
}

export function RecentComments({
  comments,
  loading,
  onInitiativeClick,
  maxVisible = 9,
}: RecentCommentsProps) {
  const visibleComments = comments.slice(0, maxVisible);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Recent Comments
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Recent Comments
        </h3>
        <Link
          to="/commentslog"
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          View All
        </Link>
      </div>

      {visibleComments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
            <ChatBubbleLeftIcon className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">No recent comments</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Comments on initiatives will appear here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleComments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              onClick={() => onInitiativeClick?.(comment.initiative.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
